import * as React from 'react';

import { createSubscriptionHook } from '~/shared/providers/api';

export type PresenceMember = {
  id: string;
  kind: string;
  name?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type PresenceEventType = 'SNAPSHOT' | 'JOIN' | 'LEAVE' | 'UPDATE';

export type PresenceEvent = {
  room: string;
  type: PresenceEventType;
  members: PresenceMember[];
};

type PresenceMemberMap = Map<string, PresenceMember>;

const usePresenceEventsSubscription = createSubscriptionHook(
  'iot.presenceEvents',
  (params: { room: string; join?: boolean; sessionId?: string }) => ({
    presenceEvents: {
      __args: {
        room: params.room,
        join: params.join,
        sessionId: params.sessionId,
      },
      room: true,
      type: true,
      members: {
        id: true,
        kind: true,
        name: true,
        metadata: true,
      },
    },
  }),
  {
    select: ({ presenceEvents }) => presenceEvents,
  },
);

function mapsEqual(
  current: PresenceMemberMap,
  next: PresenceMemberMap,
): boolean {
  if (current.size !== next.size) {
    return false;
  }

  for (const [memberID, member] of current) {
    const other = next.get(memberID);
    if (
      !other ||
      other.kind !== member.kind ||
      other.metadata !== member.metadata
    ) {
      return false;
    }
  }

  return true;
}

function applyPresenceEvent(
  current: PresenceMemberMap,
  event: PresenceEvent,
): PresenceMemberMap {
  const next = new Map(current);
  switch (event.type) {
    case 'SNAPSHOT':
      next.clear();
      for (const member of event.members) {
        next.set(member.id, member);
      }
      break;
    case 'JOIN':
      for (const member of event.members) {
        next.set(member.id, member);
      }
      break;
    case 'LEAVE':
      for (const member of event.members) {
        next.delete(member.id);
      }
      break;
    case 'UPDATE':
      for (const member of event.members) {
        const existing = next.get(member.id);
        if (existing) {
          next.set(member.id, { ...existing, metadata: member.metadata });
        }
      }
      break;
    default:
      break;
  }
  if (mapsEqual(current, next)) return current;
  return next;
}

export function useHardwarePresence(options?: {
  skip?: boolean;
  onEvent?: () => void;
}): { members: Set<string>; membersById: PresenceMemberMap } {
  const membersByIDRef = React.useRef<PresenceMemberMap>(new Map());
  const memberIDsRef = React.useRef<Set<string>>(new Set());
  const [, rerender] = React.useReducer((c: number) => c + 1, 0);

  usePresenceEventsSubscription(
    { room: 'hardware', join: false },
    {
      skip: options?.skip,
      onEvent: (event: PresenceEvent) => {
        const next = applyPresenceEvent(membersByIDRef.current, event);
        if (next !== membersByIDRef.current) {
          membersByIDRef.current = next;
          memberIDsRef.current = new Set(next.keys());
          rerender();
        }
        options?.onEvent?.();
      },
    },
  );

  return {
    members: memberIDsRef.current,
    membersById: membersByIDRef.current,
  };
}

export function useUserPresence(
  room: string | undefined,
  options?: { skip?: boolean; onEvent?: () => void; sessionId?: string },
): { members: Set<string>; membersById: PresenceMemberMap } {
  const membersByIDRef = React.useRef<PresenceMemberMap>(new Map());
  const memberIDsRef = React.useRef<Set<string>>(new Set());
  const [, rerender] = React.useReducer((c: number) => c + 1, 0);

  usePresenceEventsSubscription(
    room
      ? { room, join: true, sessionId: options?.sessionId }
      : { room: '', join: true },
    {
      skip: options?.skip || !room,
      onEvent: (event: PresenceEvent) => {
        const next = applyPresenceEvent(membersByIDRef.current, event);
        if (next !== membersByIDRef.current) {
          membersByIDRef.current = next;
          memberIDsRef.current = new Set(next.keys());
          rerender();
        }
        options?.onEvent?.();
      },
    },
  );

  return {
    members: memberIDsRef.current,
    membersById: membersByIDRef.current,
  };
}
